import apiClient from './client';
import type { AuthUser } from '@/store/useAuthStore';

// ─── Typen ──────────────────────────────────────────────────────────────────

export interface LoginResponse {
  accessToken: string;
  csrfToken: string;
  user: AuthUser;
  /**
   * Nur beim Einschalten von TOTP: die Ersatzcodes im Klartext.
   *
   * Das einzige Mal, dass es sie gibt – in der Datenbank steht nur der Hash.
   */
  ersatzcodes?: string[];
  /** Nur bei der Anmeldung MIT einem Ersatzcode gesetzt. */
  ersatzcodeBenutzt?: true;
  /** Wie viele Ersatzcodes danach noch übrig sind. */
  ersatzcodeRest?: number;
}

export interface TwoFactorRequiredResponse {
  requiresTwoFactor: true;
  methods: { totp: boolean; webauthn: boolean };
  message: string;
}

export interface TotpSetupResponse {
  secret: string;
  qrDataUrl: string;
}

export interface WebauthnCredentialInfo {
  id: string;
  deviceName: string | null;
  transports: string | null;
  createdAt: string;
}

// ─── Login ──────────────────────────────────────────────────────────────────

export async function login(
  email: string,
  password: string,
  totpCode?: string,
): Promise<LoginResponse | TwoFactorRequiredResponse> {
  const { data } = await apiClient.post<LoginResponse | TwoFactorRequiredResponse>(
    '/auth/login',
    { email, password, totpCode },
  );
  return data;
}

export async function refresh(): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/refresh');
  return data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

// ─── TOTP 2FA ──────────────────────────────────────────────────────────────

export async function setup2fa(): Promise<TotpSetupResponse> {
  const { data } = await apiClient.post<TotpSetupResponse>('/auth/2fa/setup');
  return data;
}

export async function verifySetup2fa(code: string): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/2fa/verify-setup', { code });
  return data;
}

export async function disable2fa(code: string): Promise<{ user: AuthUser }> {
  const { data } = await apiClient.post<{ user: AuthUser }>('/auth/2fa/disable', { code });
  return data;
}

// ─── Ersatzcodes ───────────────────────────────────────────────────────────
//
// Der Weg zurück, wenn das Telefon weg ist. Es gibt sie im Klartext genau
// zweimal: beim Einschalten von TOTP und beim Neu-Erzeugen. Danach steht in
// der Datenbank nur noch der Hash – abrufen lassen sie sich nicht mehr.

/** Wie viele Ersatzcodes noch unbenutzt sind. */
export async function getErsatzcodeStand(): Promise<{ uebrig: number; gesamt: number }> {
  const { data } = await apiClient.get<{ uebrig: number; gesamt: number }>('/auth/2fa/backup-codes');
  return data;
}

/**
 * Neuen Satz erzeugen. Der alte wird dabei ungültig – auch die unbenutzten.
 *
 * Braucht einen gültigen Code aus der Authenticator-App, nicht bloß eine
 * angemeldete Sitzung.
 */
export async function neueErsatzcodes(code: string): Promise<{ ersatzcodes: string[] }> {
  const { data } = await apiClient.post<{ ersatzcodes: string[] }>('/auth/2fa/backup-codes', { code });
  return data;
}

// ─── WebAuthn / Passkeys ───────────────────────────────────────────────────

export async function getWebauthnRegisterOptions(): Promise<PublicKeyCredentialCreationOptions> {
  const { data } = await apiClient.post('/auth/webauthn/register-options');
  return deserializeCreationOptions(data);
}

export async function verifyWebauthnRegistration(
  credential: PublicKeyCredential,
  deviceName: string,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/webauthn/register-verify', {
    credential: serializeRegistrationCredential(credential),
    deviceName,
  });
  return data;
}

export async function getWebauthnLoginOptions(email: string): Promise<PublicKeyCredentialRequestOptions> {
  const { data } = await apiClient.post('/auth/login/webauthn-options', { email });
  return deserializeRequestOptions(data);
}

export async function verifyWebauthnLogin(
  email: string,
  credential: PublicKeyCredential,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login/webauthn-verify', {
    email,
    credential: serializeAuthenticationCredential(credential),
  });
  return data;
}

export async function getWebauthnCredentials(): Promise<WebauthnCredentialInfo[]> {
  const { data } = await apiClient.get<WebauthnCredentialInfo[]>('/auth/webauthn/credentials');
  return data;
}

export async function deleteWebauthnCredential(id: string): Promise<{ user?: AuthUser }> {
  const { data } = await apiClient.delete<{ message: string; user?: AuthUser }>(
    `/auth/webauthn/credentials/${id}`,
  );
  return { user: data.user };
}

// ─── WebAuthn Serialisierungs-Helfer ───────────────────────────────────────

function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function deserializeCreationOptions(json: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const opts = json as Record<string, unknown>;
  return {
    ...opts,
    challenge: base64UrlToBuffer(opts.challenge as string),
    user: {
      ...(opts.user as Record<string, unknown>),
      id: base64UrlToBuffer((opts.user as Record<string, string>).id),
    },
    excludeCredentials: ((opts.excludeCredentials as Array<Record<string, unknown>>) ?? []).map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id as string),
    })),
  } as PublicKeyCredentialCreationOptions;
}

function deserializeRequestOptions(json: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const opts = json as Record<string, unknown>;
  return {
    ...opts,
    challenge: base64UrlToBuffer(opts.challenge as string),
    allowCredentials: ((opts.allowCredentials as Array<Record<string, unknown>>) ?? []).map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id as string),
    })),
  } as PublicKeyCredentialRequestOptions;
}

function serializeRegistrationCredential(cred: PublicKeyCredential) {
  const response = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufferToBase64Url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
  };
}

function serializeAuthenticationCredential(cred: PublicKeyCredential) {
  const response = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufferToBase64Url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
    },
  };
}

// ─── Rundnachrichten ─────────────────────────────────────────────────────────
//
// Betrifft nur Mitteilungen (Neuerungen, Hinweise). Betriebsnachrichten –
// Wartung, Störung, Sicherheit – kommen weiterhin an.

export async function getRundnachrichten(): Promise<{ abgemeldet: boolean }> {
  const { data } = await apiClient.get<{ abgemeldet: boolean }>('/auth/rundnachrichten');
  return data;
}

export async function setRundnachrichten(abgemeldet: boolean): Promise<{ abgemeldet: boolean }> {
  const { data } = await apiClient.patch<{ abgemeldet: boolean }>('/auth/rundnachrichten', {
    abgemeldet,
  });
  return data;
}
